import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactCrop from "react-image-crop";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";

import "react-image-crop/dist/ReactCrop.css";

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

function generateNewImage(canvas, crop) {
  if (!crop || !canvas) {
    return;
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((file) => {
      resolve(file);
    });
  });
}

function CropDialog(props) {
  const { upImg, closeCropper, isOpen, takeNewImage } = props;
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState({ unit: "%", width: 30, aspect: 1 / 1 });
  const [completedCrop, setCompletedCrop] = useState(null);

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
  }, [completedCrop]);

  const handleClose = async () => {
    const finalImage = await generateNewImage(
      previewCanvasRef.current,
      completedCrop
    );
    const file = new File([finalImage], "my_image.jpg", {
      type: "image/jpeg",
      lastModified: new Date().getTime(),
    });
    takeNewImage(file);
    closeCropper(false);
  };

  return (
    <div dir="ltr">
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        onClose={() => closeCropper(false)}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={() => closeCropper(false)}
        >
          تصویر خود را در سایز مورد نظر برش دهید
        </DialogTitle>
        <DialogContent dividers>
          <ReactCrop
            style={{
              backgroundColor: "#ffffff",
              width: "300px",
              height: "100%",
            }}
            circularCrop
            imageStyle={{
              width: "300px",
              height: "100%",
              margin: "0 auto",
            }}
            src={upImg}
            crop={crop}
            onImageLoaded={onLoad}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
          />
          <canvas
            ref={previewCanvasRef}
            style={{
              display: "none",
              width: Math.round(completedCrop?.width ?? 0),
              height: Math.round(completedCrop?.height ?? 0),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} color="primary">
            ذخیره تصویر
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default React.memo(CropDialog);
